import { AppFeaturePackageCustomerAccessComposite, AppFeaturePackageCustomerAccessEntity } from '@nk/data-entities-lib';
import * as dayjs from 'dayjs';

export function getMainAppFeaturePackageCustomerAccess(
  appFeaturePackageCustomerAccesses: AppFeaturePackageCustomerAccessEntity[],
): AppFeaturePackageCustomerAccessEntity {
  const mainPackages = appFeaturePackageCustomerAccesses
    ?.filter((a) => a.app_subscription_uuid !== null)
    .sort((a, b) => b.grace_end_date.valueOf() - a.grace_end_date.valueOf());
  if (mainPackages?.length > 0) {
    return mainPackages[0];
  }
  return null;
}

function getAppSubscriptionState(
  comp: AppFeaturePackageCustomerAccessComposite | null,
  daysUntilGraceEnds: number | null,
  daysUntilTermEnds: number | null,
): AppSubscriptionState {
  if (!comp) {
    return AppSubscriptionState.cancelled;
  }
  if (!comp.app_feature_package_customer_access.app_subscription_uuid) {
    return AppSubscriptionState.cancelled;
  }
  if (comp.app_feature_package_customer_access.disabled_flag === true) {
    return AppSubscriptionState.cancelled;
  }
  if (!daysUntilGraceEnds || daysUntilGraceEnds <= 0) {
    return AppSubscriptionState.cancelled;
  }
  // Check if we're in grace period (term has ended but grace period hasn't)
  if (daysUntilTermEnds !== null && daysUntilTermEnds <= 0 && daysUntilGraceEnds > 0) {
    return AppSubscriptionState.grace_period;
  }
  if (comp.app_feature_package.app_feature_package_key === 'app_trial') {
    return AppSubscriptionState.trial;
  }
  return AppSubscriptionState.active;
}

// A function that can be used across to give details about subscription. Exend this if you need more data.
export function appSubscriptionStateDetails(
  appFeaturePackageCustomerAccessComposites: AppFeaturePackageCustomerAccessComposite[],
): AppSubscriptionStateDetails {
  let featurePackageComp: null | AppFeaturePackageCustomerAccessComposite = null;
  let daysUntilTermEnds: number | null = null;
  let daysUntilGraceEnds: number | null = null;

  // app_subscription_uuid Should probably be filtered in backend..
  const featurePackageComps = appFeaturePackageCustomerAccessComposites.filter(
    (a) => a.app_feature_package_customer_access.app_subscription_uuid != null,
  );
  if (featurePackageComps.length === 1) {
    featurePackageComp = featurePackageComps[0];
  }
  if (featurePackageComp && featurePackageComp.app_feature_package_customer_access.term_end_date) {
    daysUntilTermEnds = getDaysTilTermExpires(featurePackageComp.app_feature_package_customer_access.term_end_date);
  }
  if (featurePackageComp && featurePackageComp.app_feature_package_customer_access.grace_end_date) {
    daysUntilGraceEnds = getDaysTilTermExpires(featurePackageComp.app_feature_package_customer_access.grace_end_date);
  }
  const appSubscriptionState = getAppSubscriptionState(featurePackageComp, daysUntilGraceEnds, daysUntilTermEnds);

  return {
    appSubscriptionState,
    daysUntilTermEnds,
    daysUntilGraceEnds,
    gracePeriodEndDate: featurePackageComp?.app_feature_package_customer_access.grace_end_date || null,
  };
}

/**
 * The diff is from clock 00.00 to 00.00. But its more natural to think that you get a full day for the first day.
 * e.g. if today is 5th jan, and it expires 10th of jan. You would expect 5 days left of trial. But diff() gives 4.
 * So we add 1 day, and if it is 0 days left, you will recieve warning.
 */
export function getDaysTilTermExpires(termEndDate: Date): number {
  const today = dayjs().startOf('day');
  const expires = dayjs(termEndDate).startOf('day');
  const daysDiff = expires.diff(today, 'days') + 1;
  return daysDiff;
}

export function hasTermExpired(termEndDate: Date): boolean {
  return getExpirationState(termEndDate).key === 'expired'; // At 0 days left, you get warning and it closes.
}

/**
 * Checks if a subscription is in grace period (term has ended but grace period hasn't)
 */
export function isInGracePeriod(termEndDate: Date, graceEndDate: Date): boolean {
  const daysUntilTermEnds = getDaysTilTermExpires(termEndDate);
  const daysUntilGraceEnds = getDaysTilTermExpires(graceEndDate);
  return daysUntilTermEnds <= 0 && daysUntilGraceEnds > 0;
}

export enum AppSubscriptionState {
  active = 'active',
  cancelled = 'cancelled',
  trial = 'trial',
  grace_period = 'grace_period',
}
export interface AppSubscriptionStateDetails {
  appSubscriptionState: AppSubscriptionState;
  daysUntilTermEnds: number | null;
  daysUntilGraceEnds: number | null;
  gracePeriodEndDate: Date | null;
}

export type ExpirationStateKey = 'less_then_week_left' | 'less_then_month_left' | 'expired' | 'active';

export interface ExpirationState {
  key: ExpirationStateKey;
  daysLeft: number;
}

/**
 * Returns subscription actual state
 */
export function getExpirationState(date: Date): ExpirationState {
  const today = dayjs();
  const expirationDate = dayjs(date);
  if (today.isAfter(expirationDate)) {
    return { key: 'expired', daysLeft: 0 };
  }
  const daysLeft = getDaysTilTermExpires(date);
  if (expirationDate.diff(today, 'week') < 1) {
    return { key: 'less_then_week_left', daysLeft };
  }
  if (expirationDate.diff(today, 'month') < 1) {
    return { key: 'less_then_month_left', daysLeft };
  }
  return { key: 'active', daysLeft };
}
